import React, { useState, useEffect, useRef } from 'react';
import { 
  Code2, 
  Eye, 
  RotateCcw, 
  Save, 
  Smartphone, 
  Monitor, 
  Send, 
  Check, 
  AlertTriangle, 
  Copy, 
  Sparkles, 
  CheckCircle2, 
  Info,
  ShieldCheck,
  FileCode,
  Undo2,
  Redo2,
  HelpCircle
} from 'lucide-react';
import { 
  DEFAULT_VERIFICATION_EMAIL_TEMPLATE, 
  TEMPLATE_VARIABLES, 
  renderVerificationEmailHtml 
} from '../../utils/emailTemplates';
import { 
  subscribeEmailTemplate, 
  saveEmailTemplateToFirestore 
} from '../../services/firestoreService';

interface EmailTemplateEditorProps {
  onSuccessToast?: (msg: string) => void;
  onErrorToast?: (msg: string) => void;
  onTriggerToast?: (title: string, message: string) => void;
}

export const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  onSuccessToast,
  onErrorToast,
  onTriggerToast,
}) => {
  // Template states
  const [templateHtml, setTemplateHtml] = useState<string>(DEFAULT_VERIFICATION_EMAIL_TEMPLATE);
  const [initialLoadedHtml, setInitialLoadedHtml] = useState<string>(DEFAULT_VERIFICATION_EMAIL_TEMPLATE);
  const [isCustomActive, setIsCustomActive] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState<boolean>(false);
  const [copiedCode, setCopiedCode] = useState<boolean>(false);

  // Undo / Redo history
  const [history, setHistory] = useState<string[]>([DEFAULT_VERIFICATION_EMAIL_TEMPLATE]);
  const [historyIndex, setHistoryIndex] = useState<number>(0);

  // Live Preview simulation test variables
  const [previewViewport, setPreviewViewport] = useState<'desktop' | 'mobile'>('desktop');
  const [simName, setSimName] = useState<string>('Alex Johnson');
  const [simEmail, setSimEmail] = useState<string>('alex.j@playgroundleague.pro');
  const [simOtp, setSimOtp] = useState<string>('849201');

  // Test send drawer / modal state
  const [showTestModal, setShowTestModal] = useState<boolean>(false);
  const [testRecipientEmail, setTestRecipientEmail] = useState<string>('');
  const [isSendingTest, setIsSendingTest] = useState<boolean>(false);
  const [testSendResult, setTestSendResult] = useState<{ success: boolean; message: string; provider?: string } | null>(null);

  // Textarea Ref for cursor manipulation
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Fetch initial template from server & subscribe to Firestore updates
  useEffect(() => {
    let isMounted = true;

    // Fetch from server API
    fetch('/api/email/template')
      .then((res) => res.json())
      .then((data) => {
        if (isMounted && data.success) {
          const current = data.customTemplate || DEFAULT_VERIFICATION_EMAIL_TEMPLATE;
          setTemplateHtml(current);
          setInitialLoadedHtml(current);
          setIsCustomActive(Boolean(data.isCustom));
          setHistory([current]);
          setHistoryIndex(0);
        }
      })
      .catch((err) => console.warn('Could not fetch server email template:', err));

    // Also listen to Firestore live sync
    const unsubscribe = subscribeEmailTemplate((storedCustom) => {
      if (!isMounted) return;
      if (storedCustom) {
        setIsCustomActive(true);
      } else {
        setIsCustomActive(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Update history on edit
  const handleCodeChange = (newVal: string) => {
    setTemplateHtml(newVal);
    // Push new history branch
    const updatedHistory = history.slice(0, historyIndex + 1);
    updatedHistory.push(newVal);
    if (updatedHistory.length > 50) updatedHistory.shift();
    setHistory(updatedHistory);
    setHistoryIndex(updatedHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const nextIndex = historyIndex - 1;
      setHistoryIndex(nextIndex);
      setTemplateHtml(history[nextIndex]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setTemplateHtml(history[nextIndex]);
    }
  };

  // Insert variable token into textarea at cursor position
  const handleInsertToken = (tokenKey: string) => {
    if (!textareaRef.current) {
      handleCodeChange(templateHtml + tokenKey);
      return;
    }
    const textarea = textareaRef.current;
    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const currentVal = textarea.value;

    const updated = currentVal.substring(0, startPos) + tokenKey + currentVal.substring(endPos);
    handleCodeChange(updated);

    // Reposition cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(startPos + tokenKey.length, startPos + tokenKey.length);
    }, 50);
  };

  // Handle Tab key in textarea for indentation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const updated = templateHtml.substring(0, start) + '  ' + templateHtml.substring(end);
      handleCodeChange(updated);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  // Compute live rendered HTML with simulation test inputs
  const liveRenderedHtml = renderVerificationEmailHtml(templateHtml, {
    recipientName: simName,
    recipientEmail: simEmail,
    verificationCode: simOtp,
    directVerifyUrl: `https://playgroundleague.pro/api/verify-email?email=${encodeURIComponent(simEmail)}&code=${simOtp}`,
    appOrigin: 'https://playgroundleague.pro',
    year: new Date().getFullYear(),
  });

  // Check for critical missing tokens
  const missingTokens: string[] = [];
  if (!templateHtml.includes('verificationCode') && !templateHtml.includes('{{verificationCode}}')) {
    missingTokens.push('{{verificationCode}}');
  }
  if (!templateHtml.includes('directVerifyUrl') && !templateHtml.includes('{{directVerifyUrl}}')) {
    missingTokens.push('{{directVerifyUrl}}');
  }

  // Save template to server & Firestore
  const handleSaveTemplate = async () => {
    setIsSaving(true);
    try {
      const res = await fetch('/api/email/template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template: templateHtml }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to save email template.');
      }

      await saveEmailTemplateToFirestore(templateHtml);
      setIsCustomActive(true);
      setInitialLoadedHtml(templateHtml);

      if (onSuccessToast) {
        onSuccessToast('Verification email template successfully saved and activated for all outgoing emails!');
      }
      onTriggerToast?.('Template Saved! 💾', 'Verification email template updated and active for all future transmissions.');
    } catch (err: any) {
      console.error('Error saving template:', err);
      if (onErrorToast) {
        onErrorToast(err.message || 'Failed to save email template.');
      }
      onTriggerToast?.('Save Error', err.message || 'Failed to save email template.');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to default
  const handleConfirmReset = async () => {
    setIsResetting(true);
    try {
      const res = await fetch('/api/email/template/reset', {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to reset email template.');
      }

      await saveEmailTemplateToFirestore(null);
      setTemplateHtml(DEFAULT_VERIFICATION_EMAIL_TEMPLATE);
      setInitialLoadedHtml(DEFAULT_VERIFICATION_EMAIL_TEMPLATE);
      setIsCustomActive(false);
      setShowResetConfirmModal(false);

      const newHist = [DEFAULT_VERIFICATION_EMAIL_TEMPLATE];
      setHistory(newHist);
      setHistoryIndex(0);

      if (onSuccessToast) {
        onSuccessToast('Reset to default official high-conversion email template.');
      }
      onTriggerToast?.('Template Reset 🔄', 'Restored official default high-conversion template.');
    } catch (err: any) {
      console.error('Error resetting template:', err);
      if (onErrorToast) {
        onErrorToast(err.message || 'Failed to reset email template.');
      }
      onTriggerToast?.('Reset Error', err.message || 'Failed to reset email template.');
    } finally {
      setIsResetting(false);
    }
  };

  // Copy HTML to clipboard
  const handleCopyHtml = () => {
    navigator.clipboard.writeText(templateHtml);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // Send live diagnostic test email with the edited template
  const handleSendTestEmail = async () => {
    if (!testRecipientEmail || !testRecipientEmail.includes('@')) {
      if (onErrorToast) onErrorToast('Please provide a valid recipient email address.');
      onTriggerToast?.('Invalid Email', 'Please provide a valid recipient email address.');
      return;
    }

    setIsSendingTest(true);
    setTestSendResult(null);

    try {
      const res = await fetch('/api/email/test-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: testRecipientEmail,
          name: simName || 'Test Athlete',
          type: 'test_email',
          customSubject: `🧪 [Template Test] Playground League Verification Code (${simOtp})`,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to dispatch test email.');
      }

      setTestSendResult({
        success: true,
        message: `Dispatched successfully to ${testRecipientEmail} via ${data.provider.toUpperCase()} (${data.latencyMs || 250}ms latency).`,
        provider: data.provider,
      });

      if (onSuccessToast) {
        onSuccessToast(`Live preview test dispatched to ${testRecipientEmail}`);
      }
    } catch (err: any) {
      console.error('Test email send error:', err);
      setTestSendResult({
        success: false,
        message: err.message || 'Dispatch failed. Check provider credentials.',
      });
      if (onErrorToast) {
        onErrorToast(err.message || 'Test send failed.');
      }
    } finally {
      setIsSendingTest(false);
    }
  };

  const lineCount = templateHtml.split('\n').length;
  const charCount = templateHtml.length;
  const isDirty = templateHtml !== initialLoadedHtml;

  return (
    <div id="email-template-editor-root" className="space-y-6">
      {/* HEADER BAR & CONTROLS */}
      <div className="bg-slate-900/90 backdrop-blur-md border border-slate-800 rounded-2xl p-5 shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-lime-400/10 border border-lime-400/30 text-lime-400">
                <Code2 className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2 className="text-xl font-black italic tracking-wide text-white uppercase">
                    Verification Email HTML Editor
                  </h2>
                  {isCustomActive ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-pulse">
                      <Sparkles className="w-3 h-3" /> Custom Template Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-lime-500/20 text-lime-400 border border-lime-500/40">
                      <ShieldCheck className="w-3 h-3" /> Official Default Template
                    </span>
                  )}
                  {isDirty && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-sky-500/20 text-sky-400 border border-sky-500/30">
                      Unsaved Changes
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Live-edit and customize the branded HTML layout, Tailwind colors, and typography for athlete OTP verification emails.
                </p>
              </div>
            </div>
          </div>

          {/* ACTIONS TOOLBAR */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              id="btn-undo-template"
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              title="Undo change (Ctrl+Z)"
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:hover:bg-slate-800 text-slate-300 border border-slate-700 transition"
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button
              id="btn-redo-template"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              title="Redo change (Ctrl+Y)"
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:hover:bg-slate-800 text-slate-300 border border-slate-700 transition"
            >
              <Redo2 className="w-4 h-4" />
            </button>
            <button
              id="btn-copy-template-html"
              onClick={handleCopyHtml}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 border border-slate-700 transition"
            >
              {copiedCode ? <Check className="w-3.5 h-3.5 text-lime-400" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedCode ? 'Copied' : 'Copy HTML'}
            </button>
            <button
              id="btn-open-test-modal"
              onClick={() => setShowTestModal(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-300 text-xs font-bold border border-indigo-500/40 transition"
            >
              <Send className="w-3.5 h-3.5" />
              Test Send
            </button>
            <button
              id="btn-reset-template-default"
              onClick={() => setShowResetConfirmModal(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs font-bold border border-rose-500/30 transition"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset to Default
            </button>
            <button
              id="btn-save-email-template"
              onClick={handleSaveTemplate}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-lime-400 hover:bg-lime-300 active:scale-95 text-slate-950 text-xs font-black uppercase tracking-wider transition shadow-lg shadow-lime-400/20 disabled:opacity-50"
            >
              {isSaving ? (
                <div className="w-3.5 h-3.5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Save & Activate
            </button>
          </div>
        </div>

        {/* DYNAMIC TOKEN INSERTION PILLS */}
        <div className="mt-4 pt-4 border-t border-slate-800/80 flex flex-col md:flex-row md:items-center gap-2">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 whitespace-nowrap">
            <Sparkles className="w-3.5 h-3.5 text-lime-400" /> Insert Variable:
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {TEMPLATE_VARIABLES.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => handleInsertToken(v.key)}
                title={`${v.label}: ${v.description} (Click to insert)`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-mono font-medium bg-slate-800 hover:bg-slate-700 text-lime-300 border border-slate-700/80 hover:border-lime-400/50 transition cursor-pointer"
              >
                <span>+</span> {v.key}
              </button>
            ))}
          </div>
        </div>

        {/* MISSING TOKEN WARNING */}
        {missingTokens.length > 0 && (
          <div className="mt-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center gap-2.5 text-xs text-amber-300">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
            <div>
              <strong className="font-bold">Missing Required Variables:</strong> The template does not contain{' '}
              {missingTokens.map((t, idx) => (
                <code key={t} className="bg-amber-950/80 text-amber-200 px-1.5 py-0.5 rounded font-mono font-bold mx-1">
                  {t}
                </code>
              ))}
              . Users may be unable to retrieve their verification code.
            </div>
          </div>
        )}
      </div>

      {/* SPLIT SCREEN WORKSPACE: EDITOR & LIVE PREVIEW */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
        {/* LEFT COLUMN: CODE EDITOR */}
        <div className="xl:col-span-6 bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col h-[750px]">
          {/* EDITOR SUB-HEADER */}
          <div className="bg-slate-950/80 px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-300 uppercase tracking-wider">
              <FileCode className="w-4 h-4 text-lime-400" />
              <span>HTML / Tailwind Inline Code</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500 font-mono">
              <span>{lineCount} lines</span>
              <span>•</span>
              <span>{(charCount / 1024).toFixed(1)} KB</span>
            </div>
          </div>

          {/* CODE TEXTAREA */}
          <div className="relative flex-1 bg-[#090d16] font-mono text-xs text-slate-200">
            <textarea
              id="email-template-code-input"
              ref={textareaRef}
              value={templateHtml}
              onChange={(e) => handleCodeChange(e.target.value)}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              className="w-full h-full p-4 bg-transparent text-slate-200 font-mono text-xs resize-none outline-none leading-relaxed border-none focus:ring-0 selection:bg-lime-400/30 selection:text-white"
              placeholder="Paste or write your HTML template here..."
            />
          </div>

          {/* EDITOR FOOTER */}
          <div className="bg-slate-950/90 px-4 py-2.5 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-lime-400 animate-pulse" />
              <span>UTF-8 • HTML5 • Universal Client Ready</span>
            </div>
            <div className="text-slate-500">
              Press <kbd className="px-1 py-0.5 bg-slate-800 text-slate-300 rounded font-mono">Tab</kbd> to indent
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: INTERACTIVE LIVE PREVIEW */}
        <div className="xl:col-span-6 bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col h-[750px]">
          {/* PREVIEW SUB-HEADER & SIMULATION CONTROLS */}
          <div className="bg-slate-950/80 px-4 py-3 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-300 uppercase tracking-wider">
              <Eye className="w-4 h-4 text-sky-400" />
              <span>Live Visual Output</span>
            </div>

            {/* VIEWPORT SELECTOR */}
            <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 self-start sm:self-auto">
              <button
                type="button"
                onClick={() => setPreviewViewport('desktop')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                  previewViewport === 'desktop'
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Monitor className="w-3.5 h-3.5" /> Desktop (600px)
              </button>
              <button
                type="button"
                onClick={() => setPreviewViewport('mobile')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                  previewViewport === 'mobile'
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Smartphone className="w-3.5 h-3.5" /> Mobile (375px)
              </button>
            </div>
          </div>

          {/* SIMULATION TEST BAR */}
          <div className="bg-slate-900/90 px-4 py-2.5 border-b border-slate-800/80 grid grid-cols-3 gap-3 text-xs">
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-0.5">
                Simulated Name
              </label>
              <input
                type="text"
                value={simName}
                onChange={(e) => setSimName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-slate-200 text-xs focus:border-lime-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-0.5">
                Simulated Email
              </label>
              <input
                type="text"
                value={simEmail}
                onChange={(e) => setSimEmail(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-slate-200 text-xs focus:border-lime-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-0.5">
                Simulated OTP
              </label>
              <input
                type="text"
                value={simOtp}
                onChange={(e) => setSimOtp(e.target.value)}
                maxLength={6}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-lime-400 font-mono font-bold text-xs focus:border-lime-400 outline-none"
              />
            </div>
          </div>

          {/* LIVE IFRAME PREVIEW CONTAINER */}
          <div className="flex-1 bg-[#020617] p-4 overflow-auto flex justify-center items-start">
            <div
              className={`transition-all duration-300 ${
                previewViewport === 'mobile'
                  ? 'w-[375px] rounded-3xl border-4 border-slate-800 shadow-2xl overflow-hidden my-auto'
                  : 'w-full max-w-[620px]'
              }`}
            >
              <iframe
                ref={iframeRef}
                title="Email Live Preview"
                srcDoc={liveRenderedHtml}
                sandbox="allow-same-origin"
                className="w-full h-[600px] border-0 rounded-2xl bg-slate-950"
              />
            </div>
          </div>
        </div>
      </div>

      {/* RESET CONFIRMATION MODAL */}
      {showResetConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <div className="flex items-center gap-3 text-rose-400">
              <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-black italic tracking-wide text-white uppercase">
                Reset Template to Default?
              </h3>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              This will overwrite your custom HTML template and restore the official Playground League high-conversion Tailwind template. Any custom styling changes will be permanently discarded.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowResetConfirmModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReset}
                disabled={isResetting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 transition shadow-lg shadow-rose-600/30"
              >
                {isResetting && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Confirm Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TEST SEND EMAIL MODAL */}
      {showTestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-lg w-full shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5 text-indigo-400">
                <Send className="w-5 h-5" />
                <h3 className="text-base font-black italic tracking-wide text-white uppercase">
                  Send Live Test Email
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowTestModal(false);
                  setTestSendResult(null);
                }}
                className="text-slate-400 hover:text-white text-sm"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-slate-300">
              Dispatch this currently rendered verification template to your own inbox to inspect font rendering, button contrast, and spam filters across Gmail, Outlook, or Apple Mail.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-400 mb-1">
                  Recipient Email Address
                </label>
                <input
                  type="email"
                  placeholder="e.g. coach@playgroundleague.pro"
                  value={testRecipientEmail}
                  onChange={(e) => setTestRecipientEmail(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-sm text-slate-200 focus:border-indigo-500 outline-none"
                />
              </div>

              {testSendResult && (
                <div
                  className={`p-3 rounded-xl border text-xs flex items-start gap-2 ${
                    testSendResult.success
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                      : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                  }`}
                >
                  {testSendResult.success ? (
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400 mt-0.5" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400 mt-0.5" />
                  )}
                  <div>{testSendResult.message}</div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowTestModal(false);
                  setTestSendResult(null);
                }}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleSendTestEmail}
                disabled={isSendingTest || !testRecipientEmail}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 transition shadow-lg shadow-indigo-600/30"
              >
                {isSendingTest ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                Dispatch Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
